import { isCancel } from "axios";
import dayjs from "dayjs";
import { OutputDateType } from "statotest-api-requests";

import { captureException } from "../utils/sentry-helpers";
import { clearPersisted, hydrate, persist } from "./measuredDataCachePersistence";

/**
 * Inkrementální in-memory cache surových dat grafů (FE-only, 0 negativních vlivů).
 *
 * Princip: per (MP × datatyp × compensated) drží souvislé pokryté časové okno + seřazené řádky.
 * Na nový request `[from, to]` dotáhne jen NEpokrytý ocásek a zbytek servíruje z cache.
 * Staví na invariantu, že **uložená surová data jsou neměnná** (potvrzeno uživatelem) — proto
 * historická část nepotřebuje TTL. Posledních `LIVE_MARGIN_SEC` se NIKDY necachuje (vždy fresh).
 *
 * Bezpečnost (proč 0 negativních vlivů):
 * - cachují se JEN surová data; filtrování (ignore-ranges/hideData/mpParams) běží až za cache
 *   → výstup identický jako dnes;
 * - contiguous-only: disjunktní request cache přepíše (nikdy neservíruje nefetchnutá data);
 * - dedup přes `dataId` (na hranici drží CACHED kopii — korektní jen díky invariantu neměnnosti;
 *   kdyby se řádek se stejným dataId serverově opravil, cache by servírovala starou hodnotu a
 *   shadow (porovnává jen dataId sety) by to nechytl → v tom případě reload/kill-switch);
 * - live margin drží recent data čerstvá;
 * - shadow mód ověří identitu cached vs fresh, než se zapne `live`;
 * - graceful degrade: jakákoli chyba/přes budget → normální fetch (dnešní chování);
 * - kill-switch `localStorage.chartCache='off'`.
 */

const LIVE_MARGIN_SEC = 15 * 60;
const PER_ENTRY_MAX_BYTES = 20 * 1024 * 1024;
const AVG_BYTES_PER_ROW = 272; // naměřeno (window.__cacheStats bytesPerRow)

type CacheMode = "off" | "shadow" | "live";

/**
 * Řízení režimu za běhu (localStorage `chartCache`): `live` (default) servíruje z cache;
 * `shadow` servíruje fresh + porovnává (verifikace); `off` bypass (kill-switch).
 */
const getMode = (): CacheMode => {
    try {
        const v = window.localStorage.getItem("chartCache");
        if (v === "off" || v === "live" || v === "shadow") { return v; }
    }
    catch { /* localStorage nedostupné */ }
    return "live"; // ověřeno (shadow 0 mismatch); `shadow`/`off` zůstávají jako opt-in přes localStorage
};

/** Paměťový budget škálovaný dle RAM zařízení; <2 GB → 0 (cache vypnutá). */
const getBudgetBytes = (): number => {
    let gb: number | undefined;
    try { gb = (navigator as unknown as { deviceMemory?: number }).deviceMemory; }
    catch { /* neznámé */ }
    if (gb === undefined) { return 25 * 1024 * 1024; } // FF/Safari nehlásí → střední
    if (gb >= 8) { return 60 * 1024 * 1024; }
    if (gb >= 4) { return 30 * 1024 * 1024; }
    if (gb >= 2) { return 12 * 1024 * 1024; }
    return 0;
};

interface CacheEntry {
    coveredFrom: number;
    coveredTo: number;
    rows: OutputDateType[]; // seřazené dle dateTimeEpoch (asc)
    bytesEst: number;
    lastUsed: number;
}

const store = new Map<string, CacheEntry>();
let totalBytes = 0;

const dedupById = (rows: OutputDateType[]): OutputDateType[] => {
    const seen = new Set<number>();
    const out: OutputDateType[] = [];
    for (const r of rows) {
        if (!seen.has(r.dataId)) { seen.add(r.dataId); out.push(r); }
    }
    return out;
};

const rowsInRange = (rows: OutputDateType[], from: number, to: number): OutputDateType[] =>
    rows.filter((r) => r.dateTimeEpoch >= from && r.dateTimeEpoch <= to);

const dropEntry = (key: string): void => {
    const e = store.get(key);
    if (e) { totalBytes -= e.bytesEst; store.delete(key); }
};

const evictToFit = (incoming: number, budget: number): void => {
    while (totalBytes + incoming > budget && store.size > 0) {
        let oldestKey: string | null = null;
        let oldest = Infinity;
        for (const [k, e] of store) { if (e.lastUsed < oldest) { oldest = e.lastUsed; oldestKey = k; } }
        if (oldestKey === null) { break; }
        dropEntry(oldestKey);
    }
};

type RawFetcher = (from: number, to: number) => Promise<OutputDateType[]>;

/** Jádro: servíruje z cache, dotahuje jen mezery. Nikdy neservíruje nefetchnutá data. */
const liveFetch = async (key: string, from: number, to: number, rawFetcher: RawFetcher, persistEnabled: boolean): Promise<OutputDateType[]> => {
    const budget = getBudgetBytes();
    if (budget === 0) { return rawFetcher(from, to); } // slabé zařízení → bez cache

    const liveEdge = dayjs().unix() - LIVE_MARGIN_SEC;
    const histTo = Math.min(to, liveEdge); // horní mez cachovatelné (historické) části
    const entry = store.get(key);

    // --- historická část [from, histTo] ---
    let histRows: OutputDateType[] = [];
    if (histTo >= from) {
        const overlaps = !!entry && !(from > entry.coveredTo || histTo < entry.coveredFrom);
        const headGap: [number, number] | null = overlaps && from < entry.coveredFrom ? [from, entry.coveredFrom] : null;
        const tailGap: [number, number] | null = overlaps && histTo > entry.coveredTo ? [entry.coveredTo, histTo] : null;

        if (overlaps && !headGap && !tailGap) {
            // plný hit — žádný fetch, žádný merge (jen touch LRU)
            entry.lastUsed = performance.now();
            histRows = rowsInRange(entry.rows, from, histTo);
        }
        else {
            // fetch mezer: head je ≤ coveredFrom, tail ≥ coveredTo, disjunkt = celé
            const [headRows, tailRows] = await Promise.all([
                headGap ? rawFetcher(headGap[0], headGap[1]) : Promise.resolve<OutputDateType[]>([]),
                tailGap ? rawFetcher(tailGap[0], tailGap[1]) : Promise.resolve<OutputDateType[]>([]),
            ]);
            const wholeRows = !overlaps ? await rawFetcher(from, histTo) : [];

            // sestavit merged (zachovává časové řazení: head ≤ entry ≤ tail)
            const merged = overlaps
                ? dedupById(headRows.concat(entry.rows, tailRows))
                : dedupById(wholeRows);
            const newFrom = overlaps ? Math.min(entry.coveredFrom, from) : from;
            const newTo = overlaps ? Math.max(entry.coveredTo, histTo) : histTo;

            // uložení (budget-aware); větší než strop NEBO než celý budget → necachovat.
            // dropEntry AŽ když novou entry fakt uložíme — jinak by se přes-cap merge
            // zahodila stará platná entry a nová se neuložila → trvalý re-fetch churn.
            const bytesEst = merged.length * AVG_BYTES_PER_ROW; // ~272 B/řádek (odhad; těžké typy víc → budget přibližný)
            if (bytesEst <= Math.min(PER_ENTRY_MAX_BYTES, budget)) {
                dropEntry(key);
                evictToFit(bytesEst, budget);
                store.set(key, { coveredFrom: newFrom, coveredTo: newTo, rows: merged, bytesEst, lastUsed: performance.now() });
                totalBytes += bytesEst;
                // Write-through do trvalé (IDB) vrstvy — fire-and-forget (nikdy neblokuje fetch).
                // JEN v `live` módu (shadow musí být bez trvalých side-efektů). Persistuje se jen
                // historická contiguous entry (živý ocásek se sem nedostane).
                if (persistEnabled) {
                    void persist({ key, coveredFrom: newFrom, coveredTo: newTo, rows: merged, bytesEst });
                }
            }
            histRows = rowsInRange(merged, from, histTo);
        }
    }

    // --- živá část (novější než liveEdge) — VŽDY fresh, necachuje se ---
    let liveRows: OutputDateType[] = [];
    if (to > liveEdge) {
        liveRows = await rawFetcher(Math.max(from, liveEdge), to);
    }

    // hist (≤ liveEdge) je celé před live (≥ liveEdge) → concat je časově seřazený; dedup hranice
    return dedupById(histRows.concat(liveRows));
};

/** Shadow: servíruje FRESH (0 riziko), ale spočítá cache a porovná (dataId set + count). */
const shadowFetch = async (key: string, from: number, to: number, rawFetcher: RawFetcher): Promise<OutputDateType[]> => {
    const fresh = await rawFetcher(from, to);
    try {
        const cached = await liveFetch(key, from, to, rawFetcher, false); // shadow nikdy nepersistuje
        const freshIds = new Set(fresh.map((r) => r.dataId));
        const cachedIds = new Set(cached.map((r) => r.dataId));
        const missing = [...freshIds].filter((id) => !cachedIds.has(id)).length;
        const extra = [...cachedIds].filter((id) => !freshIds.has(id)).length;
        if ((missing || extra || fresh.length !== cached.length) && import.meta.env.DEV) {
            // shadow je opt-in verifikační mód (dev) → console jen v DEV (tree-shaken v prod).
            console.warn(`[cache-mismatch] ${key} fresh=${fresh.length} cached=${cached.length} missing=${missing} extra=${extra}`);
        }
    }
    catch (e) {
        if (!isCancel(e) && import.meta.env.DEV) { console.warn("[cache-shadow] error:", e); }
    }
    return fresh;
};

/**
 * Jednorázová hydratace in-memory cache z trvalé (IDB) vrstvy. Naplní `store` MRU-first do
 * in-memory budgetu; NEpřepíše klíče, které už mezitím dotáhl běžící fetch (race pre-hydratace).
 * Vkládá LRU-first, ať MRU entry dostane nejnovější `performance.now()` (v session evikce až poslední).
 */
let hydrationPromise: Promise<void> | null = null;
const doHydrate = async (): Promise<void> => {
    const budget = getBudgetBytes();
    if (budget === 0) { return; } // slabé zařízení → cache vypnutá, nehydratovat
    const entries = await hydrate(budget); // MRU-first, už oříznuto na budget
    for (let i = entries.length - 1; i >= 0; i--) {
        const e = entries[i];
        if (store.has(e.key)) { continue; }
        evictToFit(e.bytesEst, budget);
        store.set(e.key, { coveredFrom: e.coveredFrom, coveredTo: e.coveredTo, rows: e.rows, bytesEst: e.bytesEst, lastUsed: performance.now() });
        totalBytes += e.bytesEst;
    }
};
const ensureHydrated = (): Promise<void> => {
    hydrationPromise ??= doHydrate().catch((e) => {
        captureException(e instanceof Error ? e : new Error(String(e)), { context: "measuredDataCache.ensureHydrated" });
    });
    return hydrationPromise;
};

// Kill-switch `off` vyčistí IDB jen JEDNOU za načtení stránky (guard). Víc netřeba: data
// dotažená po purge jsou čerstvá a reload guard resetuje (2. live→off toggle už nepurgeuje).
let offPurged = false;

/**
 * Veřejné API. `rawFetcher(from, to)` musí vrátit surové řádky pro daný rozsah (typicky
 * `getDataOutputData` se zafixovaným datatypem/signalem). Graceful degrade: cokoli selže
 * (kromě cancelu) → přímý fetch = dnešní chování.
 */
export const cachedFetch = async (key: string, from: number, to: number, rawFetcher: RawFetcher): Promise<OutputDateType[]> => {
    const mode = getMode();
    if (mode === "off") {
        // Kill-switch: plný bypass + JEDNORÁZOVĚ vyčistit trvalou vrstvu → obnoví „off/reload =
        // čistý stav" recovery, kterou persistence jinak oslabuje (viz doc). Po přepnutí zpět na
        // `live` se pak rehydratuje z prázdného IDB = čerstvá data.
        if (!offPurged) { offPurged = true; purgeMeasuredDataCache(); }
        return rawFetcher(from, to);
    }
    if (mode === "live") { await ensureHydrated(); } // hydratace JEN v live (shadow bez trvalých side-efektů)
    try {
        return mode === "shadow"
            ? await shadowFetch(key, from, to, rawFetcher)
            : await liveFetch(key, from, to, rawFetcher, true);
    }
    catch (e) {
        if (isCancel(e)) { throw e; } // cancel nechat probublat (řeší volající)
        // Cache selhala → Sentry (prod viditelnost) + graceful degrade na přímý fetch = dnešní chování.
        captureException(e instanceof Error ? e : new Error(String(e)), { context: "measuredDataCache.cachedFetch", cacheKey: key });
        return rawFetcher(from, to);
    }
};

/**
 * Vymaže cache — in-memory I trvalou (IDB) vrstvu + reset hydratace. **Volat při ODHLÁŠENÍ**:
 * durable cache je per-origin, takže na sdíleném počítači nesmí data přežít odhlášení; hit navíc
 * obchází serverovou autorizaci → jiný přihlášený uživatel nesmí dostat cache předchozího.
 */
export const purgeMeasuredDataCache = (): void => { store.clear(); totalBytes = 0; hydrationPromise = null; void clearPersisted(); };

/** Jen pro testy/diagnostiku. */
export const __clearMeasuredDataCache = (): void => { offPurged = false; purgeMeasuredDataCache(); };

/** Jen pro testy: simuluje reload — vymaže JEN in-memory + reset hydratace (IDB zůstane). */
export const __simulateReloadForTests = (): void => { store.clear(); totalBytes = 0; hydrationPromise = null; };

/** Jen pro testy/diagnostiku: snapshot stavu cache (velikost, součet bajtů, klíče + jejich okna). */
export const __measuredDataCacheStats = (): {
    size: number;
    totalBytes: number;
    entries: { key: string; coveredFrom: number; coveredTo: number; rows: number; bytesEst: number }[];
} => ({
    size: store.size,
    totalBytes,
    entries: [...store.entries()].map(([key, e]) => ({
        key, coveredFrom: e.coveredFrom, coveredTo: e.coveredTo, rows: e.rows.length, bytesEst: e.bytesEst,
    })),
});
